import { useMemo } from "react";
import { SpinningWheelUIReward } from "@cede/types";
import { API_ENDPOINT, QueryKeys } from "@cede/utils";
import { GRAND_PRIZE_COLOR } from "@/constants/spinningWheel";
import { useApi } from "@/hooks/core/useApi";
import { generateWheelColor, isBigPrize } from "@/utils/wheel";
import { useQuery } from "@tanstack/react-query";
import Decimal from "decimal.js";

interface UseWheelRewardsOptions {
  enabled?: boolean;
}

/**
 * Hook for managing wheel rewards data
 * Fetches rewards from the new /rewards endpoint
 */
export const useWheelRewards = (options?: UseWheelRewardsOptions) => {
  const apiHelper = useApi();

  const {
    data: rewardTypes = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: [QueryKeys.SPINNING_WHEEL_REWARDS],
    queryFn: async () => {
      const result = await apiHelper.get(API_ENDPOINT.SPINNING_WHEEL_REWARDS);
      return result.rewards;
    },
    enabled: options?.enabled ?? true,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Convert reward labels to UI rewards format
  const rewards = useMemo((): SpinningWheelUIReward[] => {
    if (!rewardTypes || rewardTypes.length === 0) return [];

    // Count non-grand-prize rewards for color generation
    const nonGrandPrizeRewards = rewardTypes.filter(
      ({ rewardAmount, rewardSymbol }) => !isBigPrize(new Decimal(rewardAmount), rewardSymbol),
    );
    let nonGrandPrizeIndex = 0;

    return rewardTypes.map(({ rewardAmount, rewardSymbol }, index: number) => {
      // Parse the label (e.g., "0.5 USDT" or "1 ETH")

      // Check if this is a grand prize reward using the centralized constant
      const isGrandPrize = isBigPrize(new Decimal(rewardAmount), rewardSymbol);
      let color: string;

      if (isGrandPrize) {
        color = GRAND_PRIZE_COLOR;
      } else {
        // Use color generator for non-grand-prize rewards
        color = generateWheelColor(nonGrandPrizeIndex, nonGrandPrizeRewards.length);
        nonGrandPrizeIndex++;
      }

      return {
        id: `${rewardSymbol}-${index}`,
        rewardAmount: new Decimal(rewardAmount),
        rewardSymbol,
        color,
      };
    });
  }, [rewardTypes]);

  // Check if there's a fetch error
  const fetchError = !!error;

  // Show skeletons only while loading or when there are no results (but no error)
  const visualLoading = isLoading || (rewards.length === 0 && !fetchError);

  // Memoized skeleton rewards for loading state
  const skeletonRewards = useMemo(() => {
    const SKELETON_COUNT = 5;
    return Array.from({ length: SKELETON_COUNT }, (_, index) => ({
      id: `skeleton-${index + 1}`,
      rewardAmount: new Decimal(0),
      rewardSymbol: "",
      color: generateWheelColor(index, SKELETON_COUNT),
    }));
  }, []);

  // Return appropriate rewards based on visual loading state
  const displayRewards = useMemo(() => {
    return visualLoading ? skeletonRewards : rewards;
  }, [visualLoading, rewards, skeletonRewards]);

  return {
    rewards: displayRewards,
    isLoading,
    visualLoading,
    error: error?.message ?? null,
    fetchError,
    hasRealRewards: !isLoading && rewards.length > 0 && !fetchError,
  };
};
